import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  Share2,
  Phone,
  MessageSquare,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Clock,
  User,
  Plus,
  Trash2,
  FileText,
  Printer,
  Send,
  ExternalLink,
  Pill,
  Activity,
  Heart,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Search,
  Bell,
  Command,
  Sliders,
  Check,
  TrendingDown,
  AlertTriangle,
  ArrowUpRight,
  Filter,
  CheckSquare
} from 'lucide-react';

export default function RefreshedDesignShowcase() {
  // 3 Distinct World-Class Design Styles:
  // 1. 'stripe' (Stripe/Linear precision: crisp hairline borders, high contrast, segmented controls)
  // 2. 'carbon' (Carbon Health/Alma: warm, spacious, friendly clinical cards, soft sage & indigo)
  // 3. 'minimal' (Apple Health/Editorial: pure white, clean typography, large headings, generous whitespace)
  const [designStyle, setDesignStyle] = useState('stripe');
  const [activeTab, setActiveTab] = useState('consult'); // 'consult' | 'queue' | 'emr' | 'campaign'

  // ========================================================
  // Real Patient Data (100% Crisp, Professional Clinical Data)
  // ========================================================
  const [patient] = useState({
    name: "Sarah Johnson",
    age: 34,
    gender: "Female",
    dob: "May 14, 1992",
    mrn: "MC-88219",
    phone: "+1 (923) 356-7890",
    email: "sarah.j@example.com",
    emergencyContact: "Mark Johnson (Husband, +1-923-555-0102)",
    diagnosis: "Major Depressive Disorder, Recurrent (F33.1)",
    comorbidity: "Generalized Anxiety Disorder (F41.1)",
    phq9: 14,
    gad7: 12,
    risk: "Moderate (Safety Plan Active)",
    appointmentTime: "10:00 AM - 10:45 AM (45 min)",
    visitType: "Telehealth Video Consultation"
  });

  // Call states
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callActive, setCallActive] = useState(true);

  // SOAP Note State
  const [soap, setSoap] = useState({
    subjective: "Patient reports worsening anticipatory anxiety and early morning awakenings (3:30 AM). Mentions feeling 'constantly on edge' prior to weekly executive syncs. Denies active self-harm or suicidal intent.",
    objective: "Kempt, calm posture, cooperative. Speech normal rate and volume. Affect mildly restricted, mood anxious/depressed. Thought process linear and goal-directed.",
    assessment: "Major Depressive Disorder, recurrent, moderate (F33.1) with Generalized Anxiety Disorder (F41.1). PHQ-9: 14 (Moderate), GAD-7: 12 (Moderate).",
    plan: "1. Titrate Sertraline from 25mg to 50mg daily with breakfast.\n2. Clonazepam 0.25mg PRN for acute panic (max 10 tablets/month).\n3. Socratic thought record for workplace triggers.\n4. Follow-up video consultation in 2 weeks."
  });

  // e-Prescription Pad
  const [meds, setMeds] = useState([
    { id: 1, name: "Sertraline HCl", dose: "50 mg", form: "Oral Tablet", sig: "1 tablet daily with breakfast", disp: "30 tablets", refills: 2 },
    { id: 2, name: "Clonazepam", dose: "0.25 mg", form: "Oral Tablet", sig: "0.5 - 1 tablet PRN for acute panic", disp: "10 tablets", refills: 0 }
  ]);
  const [newMedName, setNewMedName] = useState("");
  const [newMedDose, setNewMedDose] = useState("");

  const handleAddMed = (e) => {
    e.preventDefault();
    if (!newMedName.trim()) return;
    setMeds([...meds, {
      id: Date.now(),
      name: newMedName,
      dose: newMedDose || "10 mg",
      form: "Oral Tablet",
      sig: "Take once daily as directed",
      disp: "30 tablets",
      refills: 1
    }]);
    setNewMedName("");
    setNewMedDose("");
  };

  // Campaign State (Admin)
  const [campaign, setCampaign] = useState({
    enabled: true,
    trialDays: 14,
    aiQuotaPerDay: 20
  });

  // Dynamic Theme Styling Tokens
  const styles = {
    stripe: {
      wrapper: "bg-[#F8FAFC] text-slate-900 font-sans",
      card: "bg-white border border-slate-200/90 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
      header: "bg-white border-b border-slate-200/90 shadow-[0_1px_2px_rgba(0,0,0,0.03)]",
      accent: "bg-indigo-600 hover:bg-indigo-700 text-white",
      accentSoft: "bg-indigo-50 text-indigo-700 border border-indigo-100",
      pillActive: "bg-white text-slate-900 shadow-xs font-semibold",
      pillInactive: "text-slate-500 hover:text-slate-900"
    },
    carbon: {
      wrapper: "bg-[#F4F6F8] text-slate-800 font-sans",
      card: "bg-white border border-slate-100 rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]",
      header: "bg-white border-b border-slate-100 shadow-sm",
      accent: "bg-teal-700 hover:bg-teal-800 text-white",
      accentSoft: "bg-teal-50 text-teal-800 border border-teal-200",
      pillActive: "bg-teal-700 text-white font-medium shadow-xs",
      pillInactive: "text-slate-600 hover:text-slate-900"
    },
    minimal: {
      wrapper: "bg-[#FFFFFF] text-slate-900 font-sans",
      card: "bg-[#FAFAFA] border border-slate-200 rounded-2xl",
      header: "bg-white border-b border-slate-200",
      accent: "bg-slate-900 hover:bg-black text-white",
      accentSoft: "bg-slate-100 text-slate-800 border border-slate-200",
      pillActive: "bg-black text-white font-medium",
      pillInactive: "text-slate-500 hover:text-black"
    }
  }[designStyle];

  return (
    <div className={`min-h-screen ${styles.wrapper} antialiased`}>
      {/* Design Style Selector Banner */}
      <div className="bg-slate-900 text-white px-8 py-2.5 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center space-x-3">
          <span className="font-bold uppercase tracking-wider text-indigo-400">Design Studio:</span>
          <span className="text-slate-300">Compare 3 Production-Grade UI Directions:</span>
          <div className="inline-flex rounded-lg bg-slate-800 p-0.5 border border-slate-700">
            <button
              onClick={() => setDesignStyle('stripe')}
              className={`px-3 py-1 rounded-md transition font-medium ${designStyle === 'stripe' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              1. Stripe / Linear Precision
            </button>
            <button
              onClick={() => setDesignStyle('carbon')}
              className={`px-3 py-1 rounded-md transition font-medium ${designStyle === 'carbon' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              2. Carbon Health / Warm Clinical
            </button>
            <button
              onClick={() => setDesignStyle('minimal')}
              className={`px-3 py-1 rounded-md transition font-medium ${designStyle === 'minimal' ? 'bg-white text-black' : 'text-slate-400 hover:text-white'}`}
            >
              3. Apple Health / Editorial Minimal
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-slate-400">Zero AI Gibberish • 100% Real Clinical Components</span>
          <Link to="/dashboard" className="text-indigo-400 hover:text-indigo-300 flex items-center space-x-1">
            <span>Close Preview</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Primary Top Header */}
      <header className={`sticky top-0 z-40 px-8 py-3.5 flex flex-wrap items-center justify-between gap-4 ${styles.header}`}>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2.5">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-xs">
              <Heart className="w-5 h-5 fill-current" />
            </div>
            <div>
              <span className="text-base font-bold tracking-tight text-slate-900 block leading-tight">MindConnect EHR</span>
              <span className="text-[11px] font-medium text-slate-500">Clinical Practice Portal</span>
            </div>
          </div>

          <div className="h-5 w-[1px] bg-slate-200 hidden md:block" />

          {/* Flow Tabs */}
          <nav className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold">
            <button
              onClick={() => setActiveTab('consult')}
              className={`px-3.5 py-1.5 rounded-lg transition ${activeTab === 'consult' ? styles.pillActive : styles.pillInactive}`}
            >
              Live Telehealth Encounter
            </button>
            <button
              onClick={() => setActiveTab('queue')}
              className={`px-3.5 py-1.5 rounded-lg transition ${activeTab === 'queue' ? styles.pillActive : styles.pillInactive}`}
            >
              Today's Clinical Queue
            </button>
            <button
              onClick={() => setActiveTab('emr')}
              className={`px-3.5 py-1.5 rounded-lg transition ${activeTab === 'emr' ? styles.pillActive : styles.pillInactive}`}
            >
              Patient EMR Chart
            </button>
            <button
              onClick={() => setActiveTab('campaign')}
              className={`px-3.5 py-1.5 rounded-lg transition ${activeTab === 'campaign' ? styles.pillActive : styles.pillInactive}`}
            >
              Admin Trial Campaign
            </button>
          </nav>
        </div>

        {/* Doctor Status */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Telehealth Ready</span>
          </div>
          <div className="flex items-center space-x-2.5">
            <img
              src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=120&q=80"
              alt="Dr. Jenkins"
              className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200"
            />
            <span className="text-xs font-bold text-slate-800">Dr. Sarah Jenkins</span>
          </div>
        </div>
      </header>

      {/* Main Workspace Body */}
      <main className="p-8 max-w-7xl mx-auto space-y-6">

        {/* ========================================================================= */}
        {/* VIEW 1: LIVE PATIENT TELEHEALTH ENCOUNTER & NOTE WORKSPACE */}
        {/* ========================================================================= */}
        {activeTab === 'consult' && (
          <div className="space-y-6">
            {/* Patient Header Banner */}
            <div className={`${styles.card} p-5 flex flex-wrap items-center justify-between gap-4`}>
              <div className="flex items-center space-x-4">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80"
                  alt="Sarah Johnson"
                  className="w-13 h-13 rounded-xl object-cover ring-2 ring-emerald-500"
                />
                <div>
                  <div className="flex items-center space-x-3">
                    <h2 className="text-xl font-bold tracking-tight text-slate-900">{patient.name}</h2>
                    <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {patient.age} {patient.gender} • MRN: {patient.mrn}
                    </span>
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                      PHQ-9: {patient.phq9} (Moderate Depression)
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Scheduled: 10:00 AM • Primary Diagnosis: {patient.diagnosis}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => alert("Crisis protocol initiated. Safety plan opened.")}
                  className="px-3.5 py-2 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 text-xs font-semibold transition flex items-center space-x-1.5"
                >
                  <ShieldAlert className="w-4 h-4 text-rose-600" />
                  <span>Safety Plan</span>
                </button>
                <button
                  onClick={() => alert("Encounter signed and submitted to medical records.")}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition shadow-xs flex items-center space-x-1.5 ${styles.accent}`}
                >
                  <Check className="w-4 h-4" />
                  <span>Finalize & Sign Encounter</span>
                </button>
              </div>
            </div>

            {/* Split Screen Video & Documentation */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Column: Widescreen Video Session (6 Cols) */}
              <div className="lg:col-span-6 space-y-4">
                <div className={`${styles.card} p-4 space-y-4`}>
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-700 px-1">
                    <span className="flex items-center space-x-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span>Live Telehealth Feed (Encrypted)</span>
                    </span>
                    <span className="text-slate-500 font-mono">Duration: 18:42</span>
                  </div>

                  {/* Video Screen */}
                  <div className="relative rounded-xl overflow-hidden aspect-[16/10] bg-slate-950 flex items-center justify-center shadow-inner">
                    {isVideoOff ? (
                      <div className="text-center text-slate-500 space-y-2">
                        <VideoOff className="w-10 h-10 mx-auto" />
                        <p className="text-xs font-medium">Video Stream Paused</p>
                      </div>
                    ) : (
                      <img
                        src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=1000&q=80"
                        alt="Patient Sarah Johnson"
                        className="w-full h-full object-cover"
                      />
                    )}

                    {/* Patient Name Badge */}
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-medium text-white flex items-center space-x-1.5">
                      <User className="w-3.5 h-3.5 text-indigo-400" />
                      <span>Sarah Johnson (Patient)</span>
                    </div>

                    {/* Doctor Picture-in-Picture */}
                    <div className="absolute bottom-16 right-3 w-28 h-20 rounded-xl overflow-hidden border-2 border-white shadow-xl bg-slate-800">
                      <img
                        src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=300&q=80"
                        alt="Dr. Jenkins"
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Floating Frosted Glass Action Dock */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/60 shadow-lg flex items-center space-x-2">
                      <button
                        onClick={() => setIsMicMuted(!isMicMuted)}
                        className={`p-2.5 rounded-xl transition ${
                          isMicMuted ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                        title={isMicMuted ? 'Unmute' : 'Mute'}
                      >
                        {isMicMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </button>

                      <button
                        onClick={() => setIsVideoOff(!isVideoOff)}
                        className={`p-2.5 rounded-xl transition ${
                          isVideoOff ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                        }`}
                        title={isVideoOff ? 'Turn on video' : 'Turn off video'}
                      >
                        {isVideoOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                      </button>

                      <button
                        className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                        title="Share Psychoeducation Screen"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => alert("Visit ended. Ready to finalize notes.")}
                        className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition shadow-xs"
                      >
                        End Visit
                      </button>
                    </div>
                  </div>

                  {/* Ambient AI Scribe Preview */}
                  <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
                      <span className="flex items-center space-x-2">
                        <Sparkles className="w-4 h-4 text-indigo-600" />
                        <span>Ambient Clinical Transcription</span>
                      </span>
                      <span className="text-emerald-700 text-[11px] font-mono">Patient Consented ✓</span>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed italic">
                      "...Whenever my phone rings with an executive notification, my chest tightens and my heart races. It makes me feel completely exhausted..."
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column: High-Precision Clinical Workspace (6 Cols) */}
              <div className="lg:col-span-6 space-y-6">
                
                {/* SOAP Note Card */}
                <div className={`${styles.card} p-6 space-y-4`}>
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                    <h3 className="text-base font-bold text-slate-900">Psychiatric SOAP Note</h3>
                    <span className="text-xs text-slate-500 font-medium">Session #4 • Auto-saved 1m ago</span>
                  </div>

                  <div className="space-y-3.5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Subjective (S)
                      </label>
                      <textarea
                        rows={3}
                        value={soap.subjective}
                        onChange={(e) => setSoap({ ...soap, subjective: e.target.value })}
                        className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-900 leading-relaxed focus:bg-white focus:outline-none focus:border-indigo-600 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Objective (O) - Mental Status Exam
                      </label>
                      <textarea
                        rows={2}
                        value={soap.objective}
                        onChange={(e) => setSoap({ ...soap, objective: e.target.value })}
                        className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-900 leading-relaxed focus:bg-white focus:outline-none focus:border-indigo-600 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Assessment (A) & DSM-5 Formulation
                      </label>
                      <textarea
                        rows={2}
                        value={soap.assessment}
                        onChange={(e) => setSoap({ ...soap, assessment: e.target.value })}
                        className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-900 leading-relaxed focus:bg-white focus:outline-none focus:border-indigo-600 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        Plan (P) & Treatment
                      </label>
                      <textarea
                        rows={3}
                        value={soap.plan}
                        onChange={(e) => setSoap({ ...soap, plan: e.target.value })}
                        className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-900 leading-relaxed focus:bg-white focus:outline-none focus:border-indigo-600 transition"
                      />
                    </div>
                  </div>
                </div>

                {/* e-Prescription Pad Card */}
                <div className={`${styles.card} p-6 space-y-4`}>
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                    <div className="flex items-center space-x-2">
                      <Pill className="w-4 h-4 text-indigo-600" />
                      <h3 className="text-base font-bold text-slate-900">e-Prescription Pad</h3>
                    </div>
                    <span className="text-xs text-slate-500 font-medium">{meds.length} Active Prescriptions</span>
                  </div>

                  <div className="space-y-3">
                    {meds.map((m) => (
                      <div key={m.id} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-slate-900">{m.name} {m.dose}</span>
                          <button
                            onClick={() => setMeds(meds.filter(x => x.id !== m.id))}
                            className="text-slate-400 hover:text-rose-600 transition"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-600">{m.sig}</p>
                        <div className="flex items-center space-x-4 text-[11px] text-slate-500 pt-1 font-medium">
                          <span>Disp: <strong>{m.disp}</strong></span>
                          <span>Refills: <strong>{m.refills}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Medication Mini Form */}
                  <form onSubmit={handleAddMed} className="flex gap-2 pt-2 border-t border-slate-200">
                    <input
                      type="text"
                      placeholder="Drug name (e.g. Escitalopram)"
                      value={newMedName}
                      onChange={(e) => setNewMedName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-600"
                    />
                    <input
                      type="text"
                      placeholder="Dose"
                      value={newMedDose}
                      onChange={(e) => setNewMedDose(e.target.value)}
                      className="w-20 px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-600"
                    />
                    <button
                      type="submit"
                      className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold"
                    >
                      Add Rx
                    </button>
                  </form>
                </div>

              </div>

            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 2: TODAY'S PATIENT QUEUE & CASELOAD WORKSPACE */}
        {/* ========================================================================= */}
        {activeTab === 'queue' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">Today's Patient Schedule</h1>
                <p className="text-xs text-slate-500 mt-1">Friday, October 24, 2026 • 8 Consultations Scheduled</p>
              </div>
              <button
                onClick={() => alert("Schedule refreshed.")}
                className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs flex items-center space-x-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Refresh Queue</span>
              </button>
            </div>

            {/* 4 Wide Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Today's Visits</span>
                <p className="text-2xl font-extrabold text-slate-900">8 Scheduled</p>
              </div>
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">This Week</span>
                <p className="text-2xl font-extrabold text-indigo-600">34 Consults</p>
              </div>
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Caseload</span>
                <p className="text-2xl font-extrabold text-slate-900">46 Patients</p>
              </div>
              <div className={`${styles.card} p-5 space-y-1 border-rose-200 bg-rose-50/40`}>
                <span className="text-xs font-bold text-rose-700 uppercase tracking-wider">High Risk</span>
                <p className="text-2xl font-extrabold text-rose-600">2 Patients</p>
              </div>
            </div>

            {/* Patient Queue Cards */}
            <div className={`${styles.card} p-6 space-y-4`}>
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <span className="text-sm font-bold text-slate-900">Patient Appointments</span>
                <span className="text-xs text-slate-500">Sorted chronologically</span>
              </div>

              <div className="space-y-3">
                {[
                  { name: "Sarah Johnson", time: "10:00 AM", type: "Telehealth Video", status: "In Session", phq9: 14, risk: "Moderate Risk", photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&q=80" },
                  { name: "Michael Noerson", time: "11:30 AM", type: "Telehealth Video", status: "Waiting Room", phq9: 17, risk: "High Risk", photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80" },
                  { name: "Elena Rostova", time: "01:00 PM", type: "In-Person Clinic", status: "Confirmed", phq9: 8, risk: "Low Risk", photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80" }
                ].map((pt, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition bg-white flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center space-x-4">
                      <img src={pt.photo} alt={pt.name} className="w-12 h-12 rounded-xl object-cover ring-1 ring-slate-200" />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-bold text-slate-900">{pt.name}</span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            pt.risk.includes('High') ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                            {pt.risk}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3 text-xs text-slate-500 mt-1">
                          <span className="font-bold text-slate-900">{pt.time}</span>
                          <span>•</span>
                          <span>{pt.type}</span>
                          <span>•</span>
                          <span>PHQ-9: <strong>{pt.phq9}</strong></span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setActiveTab('consult')}
                        className={`px-4 py-2 rounded-xl text-xs font-bold transition ${styles.accent}`}
                      >
                        Start Video Visit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 3: PATIENT EMR & SYMPTOM TRAJECTORY */}
        {/* ========================================================================= */}
        {activeTab === 'emr' && (
          <div className="space-y-6">
            <div className={`${styles.card} p-6 space-y-4`}>
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div>
                  <h2 className="text-xl font-bold tracking-tight text-slate-900">Sarah Johnson • Longitudinal Record</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Continuous 6-month clinical psychometric tracking</p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Treatment Target: Remission
                </span>
              </div>

              {/* Progress Line Simulation */}
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
                <div className="flex items-center justify-between text-xs font-bold text-slate-700">
                  <span>PHQ-9 Depression Trajectory (Baseline: 22 → Current: 4)</span>
                  <span className="text-emerald-700 font-semibold">Remission Criteria Reached ✓</span>
                </div>

                <div className="space-y-3">
                  {[
                    { month: "Month 1 (Intake)", score: 22, note: "Severe depression, acute sleep fragmentation" },
                    { month: "Month 2 (Sertraline 25mg)", score: 16, note: "Partial response, sleep normalized" },
                    { month: "Month 3 (Sertraline 50mg)", score: 11, note: "Decreased rumination, work stamina improving" },
                    { month: "Month 4 (CBT Session 6)", score: 7, note: "Cognitive reframing operational" },
                    { month: "Month 5 (Maintenance)", score: 4, note: "Full functional recovery, sub-clinical scores" }
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2.5 bg-white rounded-lg border border-slate-200">
                      <span className="font-bold text-slate-900 w-44">{row.month}</span>
                      <div className="flex-1 max-w-xs mx-4 bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${(row.score / 25) * 100}%` }} />
                      </div>
                      <span className="font-mono font-bold text-slate-800 w-16">{row.score} / 27</span>
                      <span className="text-slate-500 text-[11px]">{row.note}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 4: ADMIN FREE-TRIAL CAMPAIGN (MATCHES USER'S ACTIVE WORK) */}
        {/* ========================================================================= */}
        {activeTab === 'campaign' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">Free-Trial Campaign Settings</h1>
                <p className="text-xs text-slate-500 mt-1">Configure terms and limits for public doctor self-onboarding trials (mindconnect.in/signup)</p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
                <span>Sign-Up Open</span>
                <input
                  type="checkbox"
                  checked={campaign.enabled}
                  onChange={(e) => setCampaign({ ...campaign, enabled: e.target.checked })}
                  className="rounded accent-indigo-600 h-4 w-4"
                />
              </label>
            </div>

            {/* 3 Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Doctor Trials</span>
                <p className="text-3xl font-extrabold text-slate-900">142</p>
                <p className="text-xs text-emerald-600 font-semibold">+18% this month</p>
              </div>
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Trial-to-Paid Conversion</span>
                <p className="text-3xl font-extrabold text-indigo-600">28.4%</p>
                <p className="text-xs text-slate-500">Above industry benchmark (22%)</p>
              </div>
              <div className={`${styles.card} p-5 space-y-1`}>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">AI Sessions Utilized Today</span>
                <p className="text-3xl font-extrabold text-slate-900">38</p>
                <p className="text-xs text-slate-500">Across active trial doctors</p>
              </div>
            </div>

            {/* Campaign Form Card */}
            <div className={`${styles.card} p-6 space-y-5`}>
              <h2 className="text-base font-bold text-slate-900">Campaign Configuration</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Trial Duration (Days)
                  </label>
                  <input
                    type="number"
                    value={campaign.trialDays}
                    onChange={(e) => setCampaign({ ...campaign, trialDays: parseInt(e.target.value) || 14 })}
                    className="w-full p-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-indigo-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">Number of free days granted upon email verification.</p>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Daily AI Sessions Quota
                  </label>
                  <input
                    type="number"
                    value={campaign.aiQuotaPerDay}
                    onChange={(e) => setCampaign({ ...campaign, aiQuotaPerDay: parseInt(e.target.value) || 20 })}
                    className="w-full p-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-indigo-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">Maximum AI avatar practice or ambient scribe sessions per doctor/day.</p>
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => alert("Free-trial campaign parameters updated successfully.")}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold shadow-xs transition ${styles.accent}`}
                >
                  Save Campaign Parameters
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
